import React, { useEffect, useState } from 'react'
import { Dialog } from '@progress/kendo-react-dialogs'
import { DropDownList } from '@progress/kendo-react-dropdowns'
import { Grid } from '@material-ui/core'
import LabelPure from '../label/label.pure'
import ButtonHLPure from '../buttonHL/buttonHL.pure'
import InputHLPure from '../inputHL/inputHL.pure'
import DropDownBtnHLPure from '../dropDownHL/dropDownHL.pure'
import { useTranslation } from 'react-i18next'
import ModalUtil from '../modal/modal.util'

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/**
 * @author Brayan Salas
 * @description Dialog to invite a new user, choosing its general role and the assistants it can access
 * @param {Function} onClose
 * @param {Function} onSubmit receives ({ email, roleId, assistantIds })
 * @param {Array} roleOptions list of { id, label }
 * @param {Array} assistants list of { id, name }
 * @param {Boolean} loadingAssistants
 */
const DialogAddUserPure = ({ onClose, onSubmit, roleOptions = [], assistants = [], loadingAssistants = false }) => {
  const modalUtil = new ModalUtil()
  const { t } = useTranslation('user-administration')
  const { t: tCommon } = useTranslation('common')
  const { t: tGlobal } = useTranslation()

  const [email, setEmail] = useState('')
  const [selectedRole, setSelectedRole] = useState(roleOptions[0] || null)
  const [selectedAssistants, setSelectedAssistants] = useState([])
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!selectedRole && roleOptions.length > 0) {
      setSelectedRole(roleOptions[0])
    }
  }, [roleOptions])

  const handleSubmit = async () => {
    const cleanEmail = email.trim().toLowerCase()

    if (!EMAIL_REGEX.test(cleanEmail)) {
      modalUtil.showModal('error', t('invalidEmail'), tGlobal('modal.pure.title.error'))
      return
    }
    if (!selectedRole) {
      modalUtil.showModal('error', t('roleRequired'), tGlobal('modal.pure.title.error'))
      return
    }

    try {
      setSaving(true)
      await onSubmit({
        email: cleanEmail,
        roleId: selectedRole.id,
        assistantIds: selectedAssistants.map(assistant => assistant.id)
      })
      onClose()
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      width={450}
      onClose={onClose}
      className="dialogFormHLPure-container dialog-add-user"
      title={t('addUser')}
    >
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <InputHLPure
            id="new-user-email"
            labelInput={tCommon('mail')}
            typeInput="email"
            valueInput={email}
            handleChangeInput={(e) => setEmail(e.target.value)}
            required
          />
        </Grid>

        <Grid item xs={12}>
          <LabelPure htmlFor="new-user-role" title={t('role')} />
          <DropDownList
            id="new-user-role"
            dataItemKey="id"
            textField="label"
            data={roleOptions}
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.value)}
            className="eticket-dropdown-field"
          />
        </Grid>

        <Grid item xs={12}>
          <DropDownBtnHLPure
            labelDropDown={t('assistants')}
            multiselect={true}
            optionsDropDown={assistants}
            onDropDownChange={(e) => setSelectedAssistants(e.value || [])}
            optionsValue={selectedAssistants}
            valueItem="id"
            nameOption="name"
            placeholder={tCommon('selectAssistants')}
            disable={loadingAssistants}
            required={false}
          />
        </Grid>

        <Grid item xs={12} className="button-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
          <ButtonHLPure
            nameBtn={tCommon('cancel')}
            onClick={onClose}
            colorScheme="neutral"
          />
          <ButtonHLPure
            primary
            nameBtn={tCommon('save')}
            onClick={handleSubmit}
            disabled={saving || !email.trim() || !selectedRole}
          />
        </Grid>
      </Grid>
    </Dialog>
  )
}

export default DialogAddUserPure
